import { Text } from '../../Text/Text.component';
import { HTMLMotionProps } from 'framer-motion';
import { TFunction } from 'i18next';
import React, { FC, PropsWithChildren } from 'react';
import { FadeInElement } from '@components/animations/FadeInElement/FadeInElement.component';
import { CommentsNumberIcon } from '@components/icons/CommentsNumber.icon';
import { ViewsNumberIcon } from '@components/icons/ViewsNumber.icon';
import { useTranslation } from '@locales/useTranslation.hook';
import { formatCount } from '@utils/number';
import classnames from 'classnames';

import styles from './ContentStat.module.scss';

const ICON_SIZE = 24;

interface Props extends HTMLMotionProps<'div'> {
  type: 'comment' | 'view';
  statCount?: number;
}

function buildStatIcon(type: Props['type']) {
  switch (type) {
    case 'comment': {
      return <CommentsNumberIcon height={ICON_SIZE} width={ICON_SIZE} />;
    }
    case 'view': {
      return <ViewsNumberIcon height={ICON_SIZE} width={ICON_SIZE} />;
    }
    default: {
      const switchComplete: never = type;
      return switchComplete;
    }
  }
}

function buildStatLabel(type: Props['type'], count: number, displayFullLabel: boolean, t: TFunction) {
  const formatedCount = formatCount(count);
  if (displayFullLabel) {
    switch (type) {
      case 'comment': {
        return t('common.comments-count', { count, formatedCount });
      }
      case 'view': {
        return t('actu.views.title', { count, formatedCount });
      }
      default: {
        const switchComplete: never = type;
        return switchComplete;
      }
    }
  }
  return formatedCount;
}

const ContentStat: FC<PropsWithChildren<Props>> = ({ type, statCount, className, ...rest }) => {
  const { t } = useTranslation(['actu']);
  const rootClass = classnames(styles['content-stat'], styles[`${type}-stat`], className);
  let children = <></>;
  if (statCount !== undefined) {
    const statIcon = buildStatIcon(type);
    const statLabel = buildStatLabel(type, statCount, true, t);
    children = (
      <>
        <div className={styles["content-stat-icon"]}>{statIcon}</div>
        <Text tag="div" variant="h5">
          {statLabel}
        </Text>
      </>
    );
  }

  return (
    <FadeInElement className={rootClass} show={statCount !== undefined} {...rest}>
      {children}
    </FadeInElement>
  );
};

export { ContentStat };
